import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
  getKanbanCards, createKanbanCard, updateKanbanCard, deleteKanbanCard,
} from '../api/client'
import Modal from './Modal'

const COLUMNS = [
  { key: 'backlog',     dot: 'bg-gray-300',     header: 'text-gray-500' },
  { key: 'todo',        dot: 'bg-gray-500',     header: 'text-gray-700' },
  { key: 'in_progress', dot: 'bg-blue-500',     header: 'text-blue-700' },
  { key: 'review',      dot: 'bg-amber-500',    header: 'text-amber-700' },
  { key: 'done',        dot: 'bg-emerald-500',  header: 'text-emerald-700' },
]

const STATUS_BORDER = {
  backlog:     'border-gray-200',
  todo:        'border-gray-300',
  in_progress: 'border-blue-200',
  review:      'border-amber-200',
  done:        'border-emerald-200',
}

function memberInitials(name) {
  if (!name) return '?'
  return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)
}

function formatHours(h) {
  if (h == null || h === '') return null
  const n = Number(h)
  if (!Number.isFinite(n)) return null
  return Number.isInteger(n) ? `${n}h` : `${n}h`
}

export default function KanbanBoard({ projectId, members, canEdit }) {
  const { t } = useTranslation(['operations', 'common'])
  const [cards, setCards] = useState([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(null)        // card object | { isNew: true, status }
  const [draggingId, setDraggingId] = useState(null)
  const [dragOverCol, setDragOverCol] = useState(null)

  const load = async () => {
    setLoading(true)
    try {
      setCards(await getKanbanCards(projectId))
    } catch {}
    setLoading(false)
  }
  useEffect(() => { load() }, [projectId])

  const cardsByStatus = (status) =>
    cards.filter(c => c.status === status)
         .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || (a.id - b.id))

  const handleDropOnColumn = async (status) => {
    const id = draggingId
    setDraggingId(null)
    setDragOverCol(null)
    if (id == null) return
    const card = cards.find(c => c.id === id)
    if (!card || card.status === status) return
    const prevStatus = card.status
    setCards(curr => curr.map(c => c.id === id ? { ...c, status } : c))
    try {
      await updateKanbanCard(id, { status })
    } catch {
      setCards(curr => curr.map(c => c.id === id ? { ...c, status: prevStatus } : c))
    }
  }

  const handleDelete = async (card) => {
    if (!confirm(t('kanban.deleteConfirm', { title: card.title }))) return
    try {
      await deleteKanbanCard(card.id)
      setCards(prev => prev.filter(c => c.id !== card.id))
    } catch {}
  }

  if (loading) {
    return <p className="text-sm text-gray-400">{t('kanban.loading')}</p>
  }

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
        {COLUMNS.map(col => {
          const colLabel = t(`kanban.columns.${col.key}`)
          const colCards = cardsByStatus(col.key)
          const isDragOver = dragOverCol === col.key
          return (
            <div
              key={col.key}
              onDragOver={e => { if (canEdit) { e.preventDefault(); setDragOverCol(col.key) } }}
              onDragLeave={() => dragOverCol === col.key && setDragOverCol(null)}
              onDrop={e => { e.preventDefault(); handleDropOnColumn(col.key) }}
              className={`bg-gray-50 rounded-xl border ${isDragOver ? 'border-gray-400 bg-gray-100' : 'border-gray-200'} flex flex-col min-h-[160px] transition-colors`}
            >
              {/* Column header */}
              <div className="flex items-center justify-between px-3 py-2.5 border-b border-gray-200">
                <div className="flex items-center gap-2 min-w-0">
                  <span className={`w-2 h-2 rounded-full ${col.dot} shrink-0`} />
                  <span className={`text-xs font-semibold uppercase tracking-wide ${col.header}`}>{colLabel}</span>
                  <span className="text-xs text-gray-400">{colCards.length}</span>
                </div>
                {canEdit && (
                  <button
                    onClick={() => setEditing({ isNew: true, status: col.key })}
                    className="text-gray-400 hover:text-gray-900 transition-colors"
                    title={t('kanban.addCardTitle', { column: colLabel })}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                    </svg>
                  </button>
                )}
              </div>

              {/* Cards */}
              <div className="flex-1 p-2 space-y-2">
                {colCards.length === 0 && (
                  <p className="text-xs text-gray-300 text-center py-3">{t('kanban.noCards')}</p>
                )}
                {colCards.map(card => (
                  <Card
                    key={card.id}
                    card={card}
                    members={members}
                    canEdit={canEdit}
                    border={STATUS_BORDER[col.key]}
                    onClick={() => canEdit && setEditing(card)}
                    onDelete={() => handleDelete(card)}
                    isDragging={draggingId === card.id}
                    onDragStart={() => setDraggingId(card.id)}
                    onDragEnd={() => { setDraggingId(null); setDragOverCol(null) }}
                    t={t}
                  />
                ))}
              </div>
            </div>
          )
        })}
      </div>

      {editing && (
        <CardEditor
          projectId={projectId}
          members={members}
          card={editing.isNew ? null : editing}
          defaultStatus={editing.isNew ? editing.status : editing.status}
          onClose={() => setEditing(null)}
          onSaved={(saved, mode) => {
            if (mode === 'create') setCards(prev => [...prev, saved])
            else setCards(prev => prev.map(c => c.id === saved.id ? saved : c))
            setEditing(null)
          }}
        />
      )}
    </>
  )
}

// ── Card ────────────────────────────────────────────────────────────────────

function Card({ card, members, canEdit, border, onClick, onDelete, isDragging, onDragStart, onDragEnd, t }) {
  const responsible = members.find(m => m.id === card.responsible_id)
  const hours = formatHours(card.estimated_hours)
  const dragProps = canEdit ? {
    draggable: true,
    onDragStart,
    onDragEnd,
  } : {}

  return (
    <div
      {...dragProps}
      onClick={onClick}
      className={`group relative bg-white rounded-lg border ${border} px-3 py-2.5 shadow-sm hover:shadow transition-all ${
        canEdit ? 'cursor-pointer' : ''
      } ${isDragging ? 'opacity-40' : ''}`}
    >
      <div className="flex items-start gap-2">
        <p className="flex-1 text-sm font-medium text-gray-900 leading-snug break-words">
          {card.title}
        </p>
        {canEdit && (
          <button
            onClick={e => { e.stopPropagation(); onDelete() }}
            className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-500 transition-opacity shrink-0"
            title={t('kanban.deleteCard')}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        )}
      </div>

      {card.notes && (
        <p className="mt-1.5 text-xs text-gray-500 line-clamp-2 leading-snug whitespace-pre-wrap">
          {card.notes}
        </p>
      )}

      <div className="mt-2 flex items-center gap-2 flex-wrap">
        {responsible ? (
          <span className="inline-flex items-center gap-1.5 text-xs">
            <span className="w-5 h-5 rounded-full bg-gray-900 text-white flex items-center justify-center text-[10px] font-semibold">
              {memberInitials(responsible.name)}
            </span>
            <span className="text-gray-600">{responsible.name.split(' ')[0]}</span>
          </span>
        ) : (
          <span className="text-xs text-gray-300">{t('kanban.unassigned')}</span>
        )}
        {hours && (
          <span className="ml-auto text-xs font-medium text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded">
            {hours}
          </span>
        )}
      </div>
    </div>
  )
}

// ── Card editor modal ──────────────────────────────────────────────────────

function CardEditor({ projectId, members, card, defaultStatus, onClose, onSaved }) {
  const { t } = useTranslation(['operations', 'common'])
  const isNew = !card
  const [title, setTitle] = useState(card?.title ?? '')
  const [status, setStatus] = useState(card?.status ?? defaultStatus ?? 'backlog')
  const [responsibleId, setResponsibleId] = useState(card?.responsible_id ?? '')
  const [hours, setHours] = useState(card?.estimated_hours ?? '')
  const [notes, setNotes] = useState(card?.notes ?? '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const titleRef = useRef(null)

  useEffect(() => { titleRef.current?.focus() }, [])

  const save = async () => {
    const trimmed = title.trim()
    if (!trimmed) { setError(t('kanban.editor.titleRequired')); return }
    setSaving(true)
    setError('')
    const payload = {
      title: trimmed,
      status,
      responsible_id: responsibleId === '' ? null : Number(responsibleId),
      estimated_hours: hours === '' || hours === null ? null : Number(hours),
      notes: notes.trim() || null,
    }
    try {
      if (isNew) {
        const saved = await createKanbanCard({ project_id: projectId, ...payload })
        onSaved(saved, 'create')
      } else {
        const saved = await updateKanbanCard(card.id, payload)
        onSaved(saved, 'update')
      }
    } catch (e) {
      setError(e.response?.data?.detail || t('kanban.editor.saveFailed'))
    }
    setSaving(false)
  }

  return (
    <Modal title={isNew ? t('kanban.editor.newCard') : t('kanban.editor.editCard')} onClose={onClose}>
      <div className="space-y-4 min-w-[420px]">
        {error && (
          <p className="px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">{error}</p>
        )}

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1.5">{t('kanban.editor.titleLabel')}</label>
          <input
            ref={titleRef}
            value={title}
            onChange={e => setTitle(e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
            placeholder={t('kanban.editor.titlePlaceholder')}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">{t('kanban.editor.statusLabel')}</label>
            <select
              value={status}
              onChange={e => setStatus(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
            >
              {COLUMNS.map(c => (
                <option key={c.key} value={c.key}>{t(`kanban.columns.${c.key}`)}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">{t('kanban.editor.responsibleLabel')}</label>
            <select
              value={responsibleId}
              onChange={e => setResponsibleId(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
            >
              <option value="">{t('kanban.editor.unassigned')}</option>
              {members.map(m => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1.5">{t('kanban.editor.hoursLabel')}</label>
          <input
            type="number"
            min="0"
            step="0.5"
            value={hours}
            onChange={e => setHours(e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
            placeholder={t('kanban.editor.hoursPlaceholder')}
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1.5">{t('kanban.editor.notesLabel')}</label>
          <textarea
            rows={5}
            value={notes}
            onChange={e => setNotes(e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 resize-y"
            placeholder={t('kanban.editor.notesPlaceholder')}
          />
        </div>

        <div className="flex gap-3 pt-1">
          <button
            onClick={save}
            disabled={saving}
            className="flex-1 bg-gray-900 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-gray-700 disabled:opacity-40 transition-colors"
          >
            {saving ? t('common:actions.saving') : isNew ? t('kanban.editor.createCard') : t('kanban.editor.saveChanges')}
          </button>
          <button
            onClick={onClose}
            className="flex-1 bg-gray-100 text-gray-700 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
          >
            {t('common:actions.cancel')}
          </button>
        </div>
      </div>
    </Modal>
  )
}
